import { SegmentType, SentenceRecordingType, TagType } from 'types/types'

import store from '@/store'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { hasMelody } from '@/util/VoiceStyles'

export function isProcessing(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return !!sentenceRecordingMetadata?.sentenceRecording?.uploadedAt && !sentenceRecordingMetadata?.sentenceRecording?.processingEndedAt
}

export function isRead(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  if (!sentenceRecordingMetadata.sentenceRecording) {
    return true
  }
  return !hasMelody(sentenceRecordingMetadata.sentenceRecording)
}

export function isSung(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return !isRead(sentenceRecordingMetadata)
}

export function isSegmentedRecordingMetadata(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return !!sentenceRecordingMetadata.sentenceRecording?.segmentedSentence
}

export function segmentationFromRecording(metadata: SentenceRecordingMetadataWithPitchedAudios): number[] {
  if (!isSegmentedRecordingMetadata(metadata)) return []
  return [...metadata.sentenceRecording!.segmentedSentence!.segments]
    .sort((a, b) => a.index - b.index)
    .map((segment: SegmentType) => segment.words)
}

export function nSentenceSegments(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): number {
  if (!isSegmentedRecordingMetadata(sentenceRecordingMetadata)) {
    return -1
  }
  return sentenceRecordingMetadata.sentenceRecording?.segmentedSentence?.segments.length ?? -1
}

export function nAudioSegments(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): number {
  return sentenceRecordingMetadata.sentenceRecording?.segments.length ?? -1
}

export function hasProcessingError(metadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return !!metadata.sentenceRecording?.processingEndedAt && !metadata.sentenceRecording?.processingSuccess
}

export function hasSegmentMismatch(metadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  const nSentence = nSentenceSegments(metadata)
  const nAudio = nAudioSegments(metadata)
  return nSentence > 0 && nAudio > 0 && nSentence !== nAudio
}

export function hasRecordingError(metadata: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return hasProcessingError(metadata) || hasSegmentMismatch(metadata)
}

export function hiddenTagNameIncludes(sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios, name: string): boolean {
  return !!sentenceRecordingMetadata.sentenceRecording?.tags.some((tag: TagType) => tag.hidden && tag.name === name)
}

export function hasEqualRecording(sentenceRecording: SentenceRecordingType): boolean {
  const sentenceId = sentenceRecording.sentence.id

  if (!store.getters.audio.hasRecordingsForSentence(sentenceId)) {
    return false
  }

  const recordingsForSentence = store.getters.audio.getRecordingListForSentence(sentenceId)

  const matchingRecordings = recordingsForSentence.filter((srmd) => {
    const sr = srmd.sentenceRecording
    if (!sr || sr.id === sentenceRecording.id) {
      return false
    }

    return (
      sr.pronunciation?.id === sentenceRecording.pronunciation?.id &&
      sr.torahMelody?.id === sentenceRecording.torahMelody?.id &&
      sr.haftarahMelody?.id === sentenceRecording.haftarahMelody?.id &&
      sr.sentenceGroupMelody?.id === sentenceRecording.sentenceGroupMelody?.id &&
      JSON.stringify([...sr.tags].sort((a, b) => a.id.localeCompare(b.id))) ===
        JSON.stringify([...sentenceRecording.tags].sort((a, b) => a.id.localeCompare(b.id)))
    )
  })

  return matchingRecordings.length > 0
}
